import React from 'react';
import { motion } from 'framer-motion';
import { Pencil, Check } from 'lucide-react';

const TONES = {
  teal: ['bg-teal-400', 'bg-teal-400/10 text-teal-600', 'text-teal-300'],
  indigo: ['bg-indigo-400', 'bg-indigo-400/10 text-indigo-600', 'text-indigo-300'],
  amber: ['bg-amber-400', 'bg-amber-400/10 text-amber-600', 'text-amber-300'],
  cyan: ['bg-cyan-400', 'bg-cyan-400/10 text-cyan-600', 'text-cyan-300'],
  emerald: ['bg-emerald-400', 'bg-emerald-400/10 text-emerald-600', 'text-emerald-300'],
  rose: ['bg-rose-400', 'bg-rose-400/10 text-rose-600', 'text-rose-300'],
  fuchsia: ['bg-fuchsia-400', 'bg-fuchsia-400/10 text-fuchsia-600', 'text-fuchsia-300'],
  sky: ['bg-sky-400', 'bg-sky-400/10 text-sky-600', 'text-sky-300'],
};
const toneBox = (tone, d) => { const t = TONES[tone] || TONES.teal; return d ? t[1].replace(/text-\S+/, t[2]) : t[1]; };

export const SectionShell = ({ id, title, icon: Icon, accent = 'teal', editing, onToggle, isDarkMode, children, aside }) => (
  <motion.section id={`bp-${id}`} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.35 }} data-testid={`bp-section-${id}`}
    className={`scroll-mt-24 rounded-2xl border p-6 md:p-8 ${isDarkMode ? 'bg-white/[0.02] border-white/[0.07]' : 'bg-white border-gray-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.03)]'}`}>
    <header className="flex items-center gap-3 mb-6">
      <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${toneBox(accent, isDarkMode)}`}><Icon size={16} /></span>
      <h3 className={`font-display text-[17px] font-semibold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
      {aside}
      {onToggle && <button type="button" onClick={onToggle} data-testid={`bp-edit-${id}`}
        className={`ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${editing
          ? 'bg-teal-500 text-white border-teal-500 hover:bg-teal-600'
          : isDarkMode ? 'border-white/10 text-gray-300 hover:text-teal-300 hover:border-teal-400/40' : 'border-gray-200 text-gray-600 hover:text-teal-600 hover:border-teal-300'}`}>
        {editing ? <><Check size={12} />Done</> : <><Pencil size={12} />Edit</>}
      </button>}
    </header>
    {children}
  </motion.section>
);

export const Label = ({ children, isDarkMode }) => (
  <p className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{children}</p>
);

export const Prose = ({ children, isDarkMode, className = '' }) => (
  <p className={`text-[14px] leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} ${className}`}>{children}</p>
);

export const BulletList = ({ items = [], isDarkMode, tone = 'teal' }) => (
  <ul className="space-y-2.5">
    {items.map((t, i) => (
      <li key={i} className={`flex gap-2.5 text-[13.5px] leading-snug ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
        <span className={`mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 ${(TONES[tone] || TONES.teal)[0]}`} />{t}
      </li>
    ))}
  </ul>
);

export const Pills = ({ items = [], tone = 'teal', isDarkMode }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((t, i) => <span key={i} className={`text-[12px] px-3 py-1 rounded-full ${toneBox(tone, isDarkMode)}`}>{t}</span>)}
  </div>
);
